import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, setStoredToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    // Lightweight browser fingerprint — used server-side to throttle
    // rapid-fire signups from the same browser. Intentionally non-unique
    // (no canvas/audio probes) — we just want a coarse signal alongside IP.
    const buildFingerprint = () => {
      try {
        const parts = [
          navigator.userAgent || "",
          navigator.language || "",
          `${window.screen?.width || 0}x${window.screen?.height || 0}`,
          new Intl.DateTimeFormat().resolvedOptions().timeZone || "",
          String(navigator.hardwareConcurrency || ""),
        ];
        // Simple djb2 hash to keep payload short and non-PII-looking.
        let h = 5381;
        for (const s of parts) for (const c of s) h = ((h << 5) + h + c.charCodeAt(0)) >>> 0;
        return h.toString(16);
      } catch {
        return "";
      }
    };

    (async () => {
      try {
        const { data } = await api.post("/auth/session", {
          session_id: sessionId,
          fingerprint: buildFingerprint(),
        });
        // Stash the session token in localStorage so subsequent requests can
        // send Authorization: Bearer (works even when ingress strips cookies).
        if (data?.session_token) setStoredToken(data.session_token);
        setUser(data.user);
        // Clean hash and go to dashboard
        window.history.replaceState({}, document.title, "/dashboard");
        navigate("/dashboard", { replace: true, state: { user: data.user } });
      } catch {
        // OAuth handshake failure — fall back to landing so user can retry.
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-white/50 font-mono text-xs uppercase tracking-[0.3em]">
        Establishing session<span className="cursor-blink" />
      </div>
    </div>
  );
}
